import { useEffect, useCallback } from "react";
import type { User } from "@/types";
import { getTenant } from "@/helpers/tenant";

// Reo.dev client ID for tracking
const REO_CLIENT_ID = "e4fe6006f75148d";

// Extend Window interface for Reo.dev
declare global {
  interface Window {
    Reo?: {
      init: (config: { clientID: string }) => void;
      identify: (user: {
        username: string;
        type: string;
        firstname: string;
        lastname: string;
        company: string;
      }) => void;
    };
  }
}

// Load Reo.dev tracking script
const loadReoScript = () => {
  if (window.Reo) return; // Already loaded

  const script = document.createElement("script");
  script.src = `https://static.reo.dev/${REO_CLIENT_ID}/reo.js`;
  script.defer = true;
  script.onload = () => {
    if (window.Reo) {
      window.Reo.init({ clientID: REO_CLIENT_ID });
    }
  };
  document.head.appendChild(script);
};

/**
 * Hook for Reo.dev tracking integration.
 * Automatically loads the Reo.dev script when enabled and provides
 * a function to identify users after successful authentication.
 *
 * @param enabled - Whether Reo.dev tracking should be enabled (typically for freemium users)
 * @returns Object containing identifyUser function
 */
export const useReoTracking = (enabled: boolean) => {
  // Load Reo.dev script when enabled
  useEffect(() => {
    if (enabled) {
      loadReoScript();
    }
  }, [enabled]);

  // Identify user with Reo.dev after successful login
  const identifyUser = useCallback(
    (user: User) => {
      if (enabled && window.Reo && user?.email) {
        window.Reo.identify({
          username: user.email,
          type: "email",
          firstname: user.first_name || "",
          lastname: user.last_name || "",
          company: getTenant(),
        });
      }
    },
    [enabled]
  );

  return { identifyUser };
};
